import { type ReactNode, useState } from 'react'
import { Button, PaginationBar, Table, Tooltip } from '@systemeio/components'
import { CheckCircleIcon, Trash02Icon, XCircleIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import EmptyList from 'shared/components/empty-list'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import type { DomainAssetsInterface } from 'shared/types/domain'
import { OrderEnum } from 'shared/types/swr-pagination-interface'
import { baseServerSideProps } from 'shared/utils/base-server-side-props'
import { formatDateTimeUtil } from 'shared/utils/date-time/format-date-time'
import { withDomainAssets } from 'shared/utils/with-domain-assets'
import { ProfileLayout } from 'modules/profile/components/profile-layout'
import { getProfilePageLayout } from 'modules/profile/layouts/get-profile-page-layout'
import CancelSubscriptionModal from 'modules/profile/manage-subscriptions/components/cancel-subscription-modal'
import { useManageSubscriptions } from 'modules/profile/manage-subscriptions/hooks/use-manage-subscriptions'
import type { ManageSubscriptionsInterface } from 'modules/profile/manage-subscriptions/types/manage-subscriptions-interface'

function ManageSubscriptions(): ReactNode {
  const { t } = useTranslation()
  const { user } = useUser()
  const authorizedUser = isAuthorizedUser(user) ? user : undefined
  const { limit, pagination, setPagination, setLimit } = usePagination({ isStaticUrl: true })
  const formatSubscriptionDate = (date: string): string | undefined =>
    formatDateTimeUtil(date, {
      dateFormat: authorizedUser?.dateFormat,
      timeFormat: authorizedUser?.timeFormat,
      timeZone: authorizedUser?.timezone,
    }).formatted

  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | null>(null)
  const renderStatus = (subscription: ManageSubscriptionsInterface): ReactNode => {
    if (user && !isAuthorizedUser(user)) {
      return <div />
    }
    return (
      <div className="flex items-center lg:ml-4">
        <Tooltip
          key="remove-tooltip"
          trigger={
            <span className="flex">
              {subscription.status === 'active' ? (
                <CheckCircleIcon className="size-[16px] text-icon-success sm:size-[20px]" />
              ) : (
                <XCircleIcon className="size-[16px] text-text-error sm:size-[20px]" />
              )}
            </span>
          }
          content={
            subscription.status === 'active' ? (
              t('profile.subscriptions.status.active')
            ) : subscription.subscriptionItems[0].deactivatedAt ? (
              <>
                {t('profile.subscriptions.status.cancelled')} -{' '}
                {formatSubscriptionDate(subscription.subscriptionItems[0].deactivatedAt)}
              </>
            ) : (
              t('profile.subscriptions.status.cancelled')
            )
          }
        />
      </div>
    )
  }
  const renderDate = (subscription: ManageSubscriptionsInterface): ReactNode =>
    user && !isAuthorizedUser(user) ? (
      <div />
    ) : (
      <span>{formatSubscriptionDate(subscription.createdAt)}</span>
    )

  const renderCancel = (subscription: ManageSubscriptionsInterface): ReactNode =>
    subscription.status === 'active' ? (
      <Tooltip
        content={t('profile.subscriptions.cancel_subscription')}
        trigger={
          <Trash02Icon className="size-[16px] text-text-secondary hover:text-text-error sm:size-[20px]" />
        }
        triggerProps={{
          render: (
            <Button
              variant="tertiary-gray"
              size="icon-sm"
              aria-label={t('profile.subscriptions.cancel_subscription')}
              onClick={() => setSelectedSubscriptionId(subscription.id)}
            />
          ),
        }}
      />
    ) : (
      <div className="size-4 sm:size-5" />
    )

  const { data: subscriptions, mutate } = useManageSubscriptions({
    limit,
    pagination,
  })

  const selectedSubscription = subscriptions?.items.find(
    subscription => subscription.id === selectedSubscriptionId,
  )

  return (
    <ProfileLayout title={t('profile.user_settings.manage_subscriptions.title')}>
      <div className="flex h-fit w-full flex-col gap-2 overflow-x-hidden rounded-md bg-bg-primary md:gap-5">
        {subscriptions?.items.length === 0 ? (
          <EmptyList />
        ) : (
          <div className="flex w-full flex-col gap-3">
            <div className="hidden lg:block" data-test-table-is-loaded={!!subscriptions}>
              <Table.Root aria-label={t('profile.user_settings.manage_subscriptions.title')}>
                <Table.Header>
                  <Table.Head className="w-[30%]">
                    {t('profile.subscriptions.title_plural')}
                  </Table.Head>
                  <Table.Head className="w-[40%]">
                    {t('profile.subscriptions.status.title')}
                  </Table.Head>
                  <Table.Head className="w-[30%]">{t('profile.subscriptions.date')}</Table.Head>
                  <Table.Head className="w-[68px]">
                    <span className="sr-only">
                      {t('profile.subscriptions.cancel_subscription')}
                    </span>
                  </Table.Head>
                </Table.Header>
                <Table.Body>
                  {subscriptions
                    ? subscriptions.items.map(subscription => (
                        <Table.Row key={subscription.id} id={subscription.id}>
                          <Table.Cell>
                            <span>
                              {subscription.subscriptionItems[0].orderItem.pricePlan.innerName}
                            </span>
                          </Table.Cell>
                          <Table.Cell>{renderStatus(subscription)}</Table.Cell>
                          <Table.Cell>{renderDate(subscription)}</Table.Cell>
                          <Table.Cell>{renderCancel(subscription)}</Table.Cell>
                        </Table.Row>
                      ))
                    : Array.from({ length: 10 }, (_, index) => (
                        <Table.Row key={index}>
                          <Table.LoaderCell />
                          <Table.LoaderCell />
                          <Table.LoaderCell />
                          <Table.LoaderCell />
                        </Table.Row>
                      ))}
                </Table.Body>
              </Table.Root>
            </div>
            <Table.Mobile.Root className="lg:hidden" data-test-table-is-loaded={!!subscriptions}>
              <Table.Mobile.Body>
                {subscriptions
                  ? subscriptions.items.map(subscription => (
                      <Table.Mobile.Card
                        key={subscription.id}
                        id={subscription.id}
                        className="bg-bg-primary hover:bg-bg-primary"
                      >
                        <Table.Mobile.Cell label={t('profile.subscriptions.title_plural')}>
                          <span>
                            {subscription.subscriptionItems[0].orderItem.pricePlan.innerName}
                          </span>
                        </Table.Mobile.Cell>
                        <Table.Mobile.Cell label={t('profile.subscriptions.status.title')}>
                          {renderStatus(subscription)}
                        </Table.Mobile.Cell>
                        <Table.Mobile.Cell label={t('profile.subscriptions.date')}>
                          {renderDate(subscription)}
                        </Table.Mobile.Cell>
                        <Table.Mobile.Cell>{renderCancel(subscription)}</Table.Mobile.Cell>
                      </Table.Mobile.Card>
                    ))
                  : Array.from({ length: 10 }, (_, index) => (
                      <Table.Mobile.Card key={index}>
                        <Table.Mobile.LoaderCell label={t('profile.subscriptions.title_plural')} />
                        <Table.Mobile.LoaderCell label={t('profile.subscriptions.status.title')} />
                        <Table.Mobile.LoaderCell label={t('profile.subscriptions.date')} />
                        <Table.Mobile.LoaderCell />
                      </Table.Mobile.Card>
                    ))}
              </Table.Mobile.Body>
            </Table.Mobile.Root>
            <div className="flex items-center justify-end gap-5 sm:gap-10">
              {subscriptions &&
                ((pagination.order !== 'prev' && subscriptions.hasMore) ||
                  pagination.order === 'prev' ||
                  pagination.startFromId !== undefined ||
                  limit !== 10) && (
                  <PaginationBar
                    limit={limit}
                    hasNextPage={
                      !!subscriptions &&
                      subscriptions.items.length > 0 &&
                      (subscriptions.hasMore || pagination.order === 'prev')
                    }
                    hasPreviousPage={
                      !!subscriptions &&
                      subscriptions.items.length > 0 &&
                      pagination.startFromId !== undefined &&
                      !(pagination.order === 'prev' && !subscriptions?.hasMore)
                    }
                    onNextPage={() =>
                      setPagination(prev => ({
                        ...prev,
                        startFromId: subscriptions?.items.at(-1)?.id,
                        order: OrderEnum.next,
                      }))
                    }
                    onPreviousPage={() =>
                      setPagination(prev => ({
                        ...prev,
                        startFromId: subscriptions?.items[0]?.id,
                        order: OrderEnum.prev,
                      }))
                    }
                    onLimitChange={nextLimit => {
                      setLimit(nextLimit)
                      setPagination(prev => ({
                        ...prev,
                        startFromId: undefined,
                        order: OrderEnum.next,
                      }))
                    }}
                  />
                )}
            </div>
          </div>
        )}
        {selectedSubscription && (
          <CancelSubscriptionModal
            mutate={mutate}
            subscription={selectedSubscription}
            opened
            onClose={() => setSelectedSubscriptionId(null)}
          />
        )}
      </div>
    </ProfileLayout>
  )
}

ManageSubscriptions.getLayout = (
  page: ReactNode,
  props: { domainAssets?: DomainAssetsInterface },
) =>
  getProfilePageLayout(page, {
    titleKey: 'profile.user_settings.manage_subscriptions.title',
    domainAssets: props.domainAssets,
  })

export const getServerSideProps = withDomainAssets(baseServerSideProps)

export default ManageSubscriptions
